package com.dynamicruntime.common.schema

import com.dynamicruntime.common.annotation.KdrPrivate
import com.dynamicruntime.common.endpoint.EP
import com.dynamicruntime.common.exception.KdrException
import com.dynamicruntime.common.util.analyzeTemplate
import com.dynamicruntime.common.util.fmt
import com.dynamicruntime.common.util.fmtD
import com.dynamicruntime.common.util.deepClone
import com.dynamicruntime.common.util.jsonArrayResult
import com.dynamicruntime.common.util.jsonMapResult
import com.dynamicruntime.common.util.Parsed
import com.dynamicruntime.common.util.parseDateResult
import com.dynamicruntime.common.util.parseDayLenientResult
import com.dynamicruntime.common.util.parseDayResult
import com.dynamicruntime.common.util.serverTimeZone
import com.dynamicruntime.common.util.splitComma
import com.dynamicruntime.common.util.toDay
import com.dynamicruntime.common.util.toJsonMap
import com.dynamicruntime.common.util.toOptBool
import com.dynamicruntime.common.util.toOptStr
import com.dynamicruntime.common.util.toStartOfDay
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlin.math.abs
import kotlin.math.floor
import kotlin.time.Instant

/**
 * Why a value failed schema validation; grows as more JSON Schema constructs are supported.
 *
 * These names **are** serialized, as the `code` of a reported failure (issue #198), so they are a wire
 * contract and renaming one is a breaking change. They were internal when this enum was written, and the
 * lower camelCase spelling is a leftover of that — kept because changing it now would be the very break the
 * previous sentence warns about.
 */
@Suppress("EnumEntryName")
enum class SchFailCode {
    missingRequired,

    /** A plain type check rejected the value before its content was ever inspected. */
    wrongType,

    /** The value's content was inspected (parsed/coerced) and found invalid -- e.g., a string that is
     *  not a recognizable date, boolean, number, or JSON structure. */
    badValue,

    invalidOption,

    /** An undeclared property was present on an object whose schema does not allow additional properties. */
    additionalProperty,

    /**
     * A **declared** property was present that this payload may not carry — the `else` side of a conditional
     * saying it is admissible only when some other field says so (issue #253).
     *
     * Its own code rather than [additionalProperty], which it superficially resembles: that one means "your
     * schema has never heard of this field", while this means "it is a real field, and not one you may set
     * right now". A client branching on the code should be able to tell a typo from a rule, and only one of
     * the two is fixed by reading the schema harder.
     */
    notAllowed,

    /**
     * The value fell short of its declared lower bound — whichever bound its type measures: `minimum` for a
     * number, `minLength` for a string, `minItems` for an array, `minProperties` for an object.
     *
     * **One code across all four pairs, rather than one per keyword.** A field has a single type, so on a
     * string this can only mean length and on an array only element count — the same reason [badValue] is not
     * too coarse despite covering dates, booleans, and JSON. Eight codes would buy a distinction the field's
     * own type already makes, at the cost of every `g-errors` author having to know which of eight applies.
     * The built-in *messages* still differ per type; only the code is shared.
     *
     * The cost, deliberately accepted: a client branching on the code alone cannot tell "too short" from "too
     * small" without also consulting the field's type. It has the schema, so it can.
     */
    belowMinimum,

    /** The value exceeded its declared upper bound; see [belowMinimum] for why the four pairs share two codes. */
    aboveMaximum,

    /**
     * A string did not match its field's `pattern` (issue #823). Its own code rather than [badValue], so a field's
     * `g-errors` can say what the pattern is *for* -- "a postal code is five digits" -- without that wording also
     * landing on an unrelated failure.
     */
    patternMismatch,

    /**
     * An array declared `uniqueItems` holds two equal elements (issue #823), or two elements of an item type declaring
     * a `g-primaryKey` share one key (issue #1521).
     */
    duplicateItem,
}

/**
 * The known JSON-Schema `$` keywords, which are treated as normal (non-exempt) keys when they appear as
 * data properties -- so they are subject to the [SchType.additionalProperties] rule and are kept on coercing.
 * Every *other* `$`-prefixed data key (e.g. `$note`) is off-contract: allowed on validating, dropped on coercing.
 *
 * **The rule, in one sentence:** a `$` key JSON Schema reserves is a keyword and means what JSON Schema says it
 * means; any other `$` key is a caller's scratch note, which a request ignores and stored input refuses
 * ([offContractKeyFailures]) rather than silently dropping.
 *
 * `$comment` is the reserved keyword whose job is a note for maintainers, kept with the document (issue #1135). So
 * it is kept wherever it is accepted, and it is accepted only where it is declared: on a schema node, where the
 * parser reads past it, and on the objects of a client's configuration that declare it with
 * [SchTypeBuilder.commentProperty] -- a workflow, a task, an eligibility test, a report -- or list it among their
 * keys, as a `g-layout` block and field entry do. Anywhere else it is an additional property like any other key,
 * refused with [commentRefusedMessage].
 */
@KdrPrivate
val reservedSchemaKeys: Set<String> = setOf(
    SCH.dSchema, SCH.dId, SCH.dRef, SCH.dDefs, SCH.dAnchor, SCH.dDynamicRef, SCH.dDynamicAnchor,
    SCH.dVocabulary, SCH.dComment,
)

/**
 * Why a `$comment` was refused where it was written (issue #1135): an additional property like any other key, but
 * one an author wrote on purpose, so the message says where a comment does go rather than only that it may not go
 * here. Keep the list in step with the objects that declare `$comment` (see [reservedSchemaKeys]).
 */
const val commentRefusedMessage: String =
    $$"A '$comment' is not accepted here. A comment goes on a workflow, a task, an eligibility test, a report, a " +
        "'g-layout' block or field entry, or a schema."

/**
 * A single validation failure: the [path] through the data to the offending value
 * (e.g. "address.street" or "tags[1]"), a [code] for why, and a human [message].
 * For an [SchFailCode.invalidOption] failure, [options] carries the full list of
 * currently valid choices, given first-class visibility to the caller. When a
 * [SchFailCode.badValue] came from a failed parse, [cause] carries that exception
 * (which itself holds the offset/line of the parse error).
 *
 * **A [message] is a complete sentence, sentence-cased and ending in a period**, as every other message in
 * this codebase is. These were once lowercase unpunctuated fragments in the style errors take when they are
 * only ever wrapped and concatenated — which was right while a failure was read solely as the tail of
 * `path: message`. Since issue #197 a failure has also stood on its own beneath the field that caused it, beside
 * field descriptions written as sentences, and there a fragment reads as truncated. The composed forms lose
 * nothing: `input.score: This must be of type 'integer'.` reads correctly.
 *
 * A message that opens by quoting a supplied value begins with the quote rather than a capital
 * (`'abc' is not a valid number.`). That is deliberate — the value is the caller's, not ours to re-case.
 */
data class SchFailure(
    val path: String,
    val code: SchFailCode,
    val message: String,
    val options: List<SchOption>? = null,
    val cause: KdrException? = null,
    /**
     * The schema's own wording for this failure, from the field's `g-errors` block, or null when it declares
     * none (issue #202). Sits *beside* [message] rather than replacing it, because the two serve different
     * readers: a surface documenting the wire wants to say the value is not an integer, while a form asking
     * someone for a score wants whatever the schema author wrote instead.
     */
    val userMessage: String? = null,
    /**
     * A **form requirement**, not the schema's (issue #1022): the data is valid, but the form it was judged for asks
     * for more -- a field its layout requires, a choice it does not offer ([formRequirementFailures]). The codes are
     * the schema's own, so a layout's `errors` override words both kinds alike; this is what tells them apart.
     */
    val formRequirement: Boolean = false,
)

/**
 * This failure as a plain JSON-ready map, for reporting one to a caller (issue #198).
 *
 * Lives here beside [SchFailure] rather than in the HTTP layer so the wire shape is defined **once**: the same
 * kernel both sides already share for validating also says what a reported failure looks like, which is what
 * keeps a client's reading of it from drifting from a server's writing of it.
 *
 * [SchFailure.cause] is not included — see `EP.failures`.
 */
fun SchFailure.toWireMap(): Map<String, Any?> {
    // linkedMapOf, not buildMap: the order the reader wants is path first, and buildMap's builder does not
    // promise to keep insertion order. Nothing depends on it -- JSON objects are unordered -- but a caller
    // reading a list of these by eye finds the field faster when they all start the same way.
    val out = linkedMapOf<String, Any?>(
        EP.failurePath to path,
        EP.failureCode to code.name,
        EP.failureMessage to message,
    )
    userMessage?.let { out[EP.failureUserMessage] = it }
    options?.let { opts ->
        out[EP.failureOptions] = opts.map { linkedMapOf(SCH.value to it.value, SCH.label to it.label) }
    }
    // Written only when set: a schema failure -- every one a plain validation reports -- carries nothing new.
    if (formRequirement) out[EP.failureFormRequirement] = true
    return out
}

/**
 * A one-line summary of [failures] for the envelope's `errorMessage`, naming the fields rather than dumping
 * them. The detail travels structured under `extraData`; this is what a person reads in a log line, and a
 * list of paths is the part that makes such a line worth having.
 */
fun failureSummary(failures: List<SchFailure>): String {
    val paths = failures.map { it.path.ifEmpty { "(root)" } }.distinct()
    val subject = if (paths.size == 1) "field" else "fields"
    return "Validation failed for ${paths.size} $subject: ${paths.joinToString(", ")}."
}

/**
 * A bad-input error (400) that says [message] and carries [failures] structured, under [EP.failures] of its extra
 * data (issue #198) -- how every refusal of schema-invalid input is reported, a request's own fields and a written
 * configuration's slots alike (issue #1051), so a caller reads one shape wherever the input was judged.
 */
fun inputFailuresException(message: String, failures: List<SchFailure>): KdrException =
    KdrException.mkInput(message).also { it.extraData[EP.failures] = failures.map { f -> f.toWireMap() } }

/**
 * A failure for each key of [data] the validator lets through as **off-contract** -- one starting with `_` or `$`,
 * which on a request is a caller's own annotation and is never an additional property (issue #1051).
 *
 * For input that is **stored in a canonical form**, where there is no such thing: the key would be accepted and
 * then gone, which is the silent drop a strict reading exists to end. A caller holding such input adds these to
 * what [validate] found. Only [data]'s own keys: a `$ref` inside a schema body, or a key inside free content, is
 * that content's business. A reserved keyword ([reservedSchemaKeys]) is not off-contract -- [validate] judges it as
 * the ordinary key it is -- so it is not reported here a second time.
 */
fun offContractKeyFailures(data: Map<String, Any?>): List<SchFailure> =
    data.keys.filter { it.startsWith("_") || (it.startsWith("$") && it !in reservedSchemaKeys) }
        .map { SchFailure(it, SchFailCode.additionalProperty, "Additional property '$it' is not allowed.") }

/**
 * The schema's wording for [code] on this field: the specific message, else the field's `default`, else null
 * to leave the validator's own words in place. Three levels deep and deliberately no deeper — the built-in
 * message *is* the global default, so a type-level layer would buy nothing that is not already covered.
 */
@KdrPrivate
fun SchType.userMessage(code: SchFailCode): String? =
    errorMessages[code.name] ?: errorMessages[SCH.errorDefault]

/**
 * The `g-errors` message for [code], with its `${'$'}{…}` substitutions resolved against **this failure's** params
 * (issue #589): the offending [value], the [options] list, and this field's bound -- the same per-failure-code
 * vocabulary ([errorContextData]) a layout error override uses, evaluated here in the validator so the `400`
 * envelope and a form's client-side validation get one resolved copy. [path]'s last segment is the field name.
 *
 * Fail-safe, like all schema-supplied copy: plain text (no `${'$'}`) is returned untouched, and a template the
 * failure cannot fill -- a `${'$'}{value}` where there is no value -- shows **as written** rather than blanking the
 * message. Only literal `${'$'}{…}` in a well-formed template is meant here; the boot check ([errorMessageProblems])
 * already refused a malformed one, a fragment pull, or a placeholder outside the code's vocabulary.
 */
private fun SchType.resolvedUserMessage(
    code: SchFailCode,
    path: String,
    value: Any?,
    options: List<SchOption>?,
): String? {
    val template = userMessage(code) ?: return null
    if ('$' !in template) {
        return template
    }
    val field = path.substringAfterLast('.').substringBefore('[')
    // Copy whose substitution fails (a placeholder the context does not supply) is shown as written.
    return template.analyzeTemplate(evaluateWith = errorContextData(code, this, field, value, options)).value
        ?: template
}

/**
 * A failure against this field, carrying whatever the field's `g-errors` says about [code] -- with its `${'$'}{…}`
 * params resolved against the failure (issue #589; see [resolvedUserMessage]). Used in place of constructing
 * [SchFailure] directly so that resolution cannot be forgotten at one site out of nineteen — the kind of
 * omission whose only symptom is one message quietly not being customizable. [value] is the offending value the
 * `${'$'}{value}` param renders; it defaults to null (a failure with no value to show).
 */
@KdrPrivate
fun SchType.failure(
    path: String,
    code: SchFailCode,
    message: String,
    options: List<SchOption>? = null,
    value: Any? = null,
    cause: KdrException? = null,
): SchFailure = SchFailure(path, code, message, options, cause, resolvedUserMessage(code, path, value, options))

/**
 * The problems with a field's `g-errors` message **templates** (issue #589) -- the boot check for §10's
 * substitution, walking [type] and every type beneath it (its properties, array items, union branches) because
 * `g-errors` sits on a *field's* type, at any depth. For each message: a malformed `${'$'}{…}`, a fragment pull
 * (`${'$'}{@t}`, not supported in a g-errors message), and a `${'$'}{…}` the failure code cannot provide are each
 * reported -- the same "parses clean, renders wrong" the layout copy checks prevent, against the shared
 * [errorContextNames] vocabulary. The *keys* are already validated against the code enum by `parseErrorMessages`.
 *
 * [backendPrefix] (when given) additionally refuses a backend block in a message -- a `%{…}` that this stage does
 * not resolve and would ship raw. Passed as a bare `Char` rather than referencing the content layer's constant,
 * so this stays in the kernel beside the vocabulary it checks. [seen] guards shared `$ref` types against being
 * walked twice; a caller checking many top-level types shares one set so each type is checked once.
 */
@KdrPrivate
fun errorMessageProblems(
    where: String,
    type: SchType,
    seen: MutableList<SchType> = ArrayList(),
    backendPrefix: Char? = null,
): List<String> {
    val problems = mutableListOf<String>()
    fun walk(w: String, t: SchType) {
        if (seen.any { it === t }) {
            return
        }
        seen.add(t)
        for ((codeKey, message) in t.errorMessages) {
            // A valid key is a SchFailCode name or `default` (code == null), which may match any failure.
            val code = SchFailCode.entries.firstOrNull { it.name == codeKey }
            val allowed = errorContextNames(code, t)
            problems.addAll(errorMessageTemplateProblems(w, codeKey, message, allowed, backendPrefix))
        }
        for ((name, prop) in t.properties) walk("$w field '$name'", prop.valueType)
        t.itemType?.let { walk("$w item", it) }
        t.additionalValueType?.let { walk("$w value", it) }
        t.variants?.let { v ->
            for (b in v.branches) walk("$w branch", b)
            v.defaultBranch?.let { walk("$w branch", it) }
        }
    }
    walk(where, type)
    return problems
}

/**
 * The problems with **one** `g-errors` message (issue #589): a malformed `${'$'}{…}`, a fragment pull, a backend
 * block (when [backendPrefix] is given), and a `${'$'}{…}` naming something outside [allowed] -- the names a
 * [codeKey] failure provides ([errorContextNames]). Shared by [errorMessageProblems], which walks compiled types,
 * and the stored-config repair pass, which walks a client's raw definitions to drop a bad message before it
 * compiles (issue #841). [w] names the location.
 */
@KdrPrivate
fun errorMessageTemplateProblems(
    w: String,
    codeKey: String,
    message: String,
    allowed: Set<String>,
    backendPrefix: Char? = null,
): List<String> {
    val problems = mutableListOf<String>()
    val analysis = message.analyzeTemplate()
    for (issue in analysis.issues) {
        problems.add("$w: the '${SCH.errors}' message for '$codeKey' is a malformed template: ${issue.message}")
    }
    if (analysis.refs.isNotEmpty()) {
        problems.add(
            $$"$$w: the '$${SCH.errors}' message for '$$codeKey' uses a fragment pull ('${@t}'); " +
                "a g-errors message supports only parameter substitution (see #589).",
        )
    }
    if (backendPrefix != null && backendPrefix in message && message.analyzeTemplate(backendPrefix).blockCount > 0) {
        problems.add(
            "$w: the '${SCH.errors}' message for '$codeKey' uses a backend block ('$backendPrefix{…}'); " +
                "a g-errors message supports only frontend parameter substitution (see #589).",
        )
    }
    for (p in analysis.paths.required + analysis.paths.optional) {
        val name = p.substringBefore('.')
        if (name !in allowed) {
            problems.add(
                $$"$$w: the '$${SCH.errors}' message for '$$codeKey' references '${$$p}', but a " +
                    $$"'$$codeKey' failure provides $${allowed.sorted().joinToString(", ")}.",
            )
        }
    }
    return problems
}

/**
 * Knobs that adjust a validation run for a caller whose needs differ from the plain reading of a schema -- the
 * request path, on the wire and in the interactive endpoint form (issue #191), an editor, a cross-client reader.
 *
 * Most options change only the coerced output or how something is reported, but some do change a verdict:
 * [forInput] reads a type as a **request**, so a `g-derived` property is not required and a plain string is
 * trimmed before it is measured (issue #765); [skipCompleteness] defers a fragment's completeness to where it
 * is assembled; and [allowUnknownVariant] off refuses an unknown branch the document admits. This is an object
 * rather than a parameter because the list is expected to grow as the frontend asks for more.
 *
 * A data class so a variant is derived with `copy`, which carries every other field along. Rebuilding one field by
 * field once dropped [existingTypes] under an `optionalContents` property (issue #812), and a field added later
 * would have been dropped the same way.
 */
data class SchOpts(
    /**
     * Keep an undeclared property in the coerced output instead of dropping it. It is reported as an
     * [SchFailCode.additionalProperty] failure either way.
     *
     * The wire wants it gone: an endpoint should not be handed keys its schema never declared. An editor wants
     * it kept, because dropping it rewrites the text under the person who typed it -- leaving an error about a
     * key that is no longer on screen, and nothing they can act on.
     */
    val keepAdditionalProperties: Boolean = false,
    /**
     * Validate this as a **request** rather than as a response (issue #254), which is what makes a
     * `g-derived` property behave differently in each direction: on the way in it is not asked for and not
     * accepted, on the way out it is an ordinary value.
     *
     * A flag rather than a separate projected type, and the reason is the requirement itself: a derived field
     * a client sends must be **dropped**, not refused. Dropping it means the validator has to know the
     * property exists — so the type it validates against still declares the field, while the schema published
     * to that client does not. Those two artifacts are genuinely different, and a projection that removed the
     * property from both would turn a silent strip into an `additionalProperty` failure.
     */
    val forInput: Boolean = false,
    /**
     * Whether a discriminator value naming no branch may fall to the union's default branch — that is,
     * whether this reader honors the `defaultMapping` the document declares (issue #301).
     *
     * **Defaults to true, and that is not laxity.** A union that declares a default branch *says so in the
     * emitted JSON Schema*, so a stock validator reading our document accepts an unknown value. Defaulting
     * this to false would make our own reading disagree with the document we publish, which is the one
     * property #252 was built to preserve: a branch alone reaches the same verdict as the union, and the
     * document means the same thing to somebody else's validator as it does to ours.
     *
     * So strictness is a **policy a reader applies**, deliberately, on top of what the document says — and
     * one that has to be reflected at the export boundary by stripping the default branch, or we publish a
     * schema we do not honor.
     *
     * When to be strict: inside a single client, every trait is known, so an unrecognized one is a caller
     * error and reporting it is a kindness. Across clients — an administrative surface looking at data from a
     * client whose config this deployment never loaded — an unknown trait is the architecture working, and
     * refusing it would take down a payload over an entry nobody asked about.
     */
    val allowUnknownVariant: Boolean = true,
    /**
     * Skip **this** object's completeness checks -- its `required` and its conditional (`if`/`then`/`else`)
     * requiredness -- because it is the value of a property marked [SCH.optionalContents], a fragment whose
     * completeness is settled where it is assembled into a whole, not here (issue #487). Its fields are still
     * validated, and a schema-supplied `default` is still injected; only the demand that missing fields be
     * present is waived. A conditional is waived whole rather than by half, because the value it keys on may
     * live in the stored data the fragment folds into, not in the fragment -- so it cannot be judged here at
     * all. Never set directly; [validateObject] turns it on for exactly the one object under such a property
     * and off again for anything nested inside, via [withSkipCompleteness], so the relaxation does not leak
     * down the tree.
     */
    val skipCompleteness: Boolean = false,
    /**
     * The zone a day crosses to a moment in, and a moment to its day (issue #1115): the client's, where the data is
     * a client's -- a day entered into a timestamp field begins at midnight where the client's people are. The
     * server's when not given, as before.
     */
    val zone: TimeZone = serverTimeZone,
    /**
     * The types a `g-schemaDocument` value may `$ref` (issue #316) -- the compiled store's, supplied by a
     * caller that has one. Empty by default, so a document referring to anything outside itself fails to
     * parse: honest for a standalone check, and a caller with a live store passes that store's types.
     */
    val existingTypes: Map<String, SchType> = emptyMap(),
    /**
     * Take a `g-schemaDocument` value as the object it is, **without parsing it** (issue #1052). For a caller that
     * holds a schema body apart from the document it belongs to -- one entry of a configuration being written --
     * where parsing it alone would refuse it for naming a sibling type, or for being an alteration of another
     * type, and where the whole document is compiled and judged right after. That it is an object is still
     * checked; that it is a schema is then the later check's to say, and this run makes no claim about it.
     *
     * **Its shape is still checked** (issue #1056, [SchMetaSchema.structureFailures]), and for the same reason the
     * body is not parsed alone, **a directive at its top stands**: a `g-extends` or `g-merge` there is read when the
     * configuration's types are assembled -- a type's `schema` and a trait's `dataSchema` alike, each of which
     * becomes a type of the document -- so whether it may be there is that assembly's to say. Both follow from the
     * one fact this flag states: the body is a piece of a document, held apart from it.
     */
    val schemaDocumentsUnparsed: Boolean = false,
) {
    /**
     * These options with [skipCompleteness] set to [v] -- the same instance when it already matches, so the
     * common descent (into a property that is not a fragment, from a parent that was not one) allocates nothing.
     */
    fun withSkipCompleteness(v: Boolean): SchOpts =
        if (v == skipCompleteness) this
        else copy(skipCompleteness = v)
}

/** Result of a coercing validation: the (possibly transformed) [value] and the [failures]. */
data class SchResult(val value: Any?, val failures: List<SchFailure>)

/**
 * Validates [data] against the parsed [type], collecting EVERY failure (not
 * fail-fast). Does not alter the data. Reads only declared attributes on [SchType]
 * / [SchProperty] — it never looks a value up in a raw schema map.
 *
 * Even in validate-only mode, `allowCoerce` still governs validation: a coercible field is checked with
 * its coercion rules (e.g., a boolean field with `allowCoerce` accepts "yes"), the transformed value is
 * simply not returned.
 */
fun validate(type: SchType, data: Any?, opts: SchOpts = SchOpts()): List<SchFailure> {
    val failures = mutableListOf<SchFailure>()
    validateValue(type, data, "", coerce = false, failures, opts)
    return failures
}

/**
 * Validates AND coerces [data]: applies `allowCoerce` conversions (string -> Long / Double / Boolean /
 * Date / list / map) and injects `default` values for missing properties, returning the transformed
 * value plus the failures. The input is never mutated — new maps/lists are built where anything changes.
 *
 * Validation and coercion share one pass: a value is parsed at most once, its failures recorded, and the
 * parsed result reused for the output (kept only when [coerceAndValidate] asked for it).
 */
fun coerceAndValidate(type: SchType, data: Any?, opts: SchOpts = SchOpts()): SchResult {
    val failures = mutableListOf<SchFailure>()
    val value = validateValue(type, data, "", coerce = true, failures, opts)
    return SchResult(value, failures)
}

/** Validates [value] against [type], returning the (possibly coerced) value. */
@KdrPrivate
fun validateValue(
    type: SchType, value: Any?, path: String, coerce: Boolean, failures: MutableList<SchFailure>, opts: SchOpts,
): Any? {
    val jsonType = type.jsonType

    // A discriminated union: pick the branch, then validate against that one alone (issue #252).
    type.variants?.let { variants ->
        return validateVariant(type, variants, value, path, coerce, failures, opts)
    }

    // A string field carrying a date format is validated by parsing (and optionally becomes a Date).
    if (jsonType == SCT.string && isDateFormat(type.format)) {
        return validateDate(type, value, path, coerce, failures, opts.zone)
    }

    // File content passes through untouched. It is spelled `type: string` because that is how OpenAPI spells a
    // file (see SFMT.binary), but the value is not text -- it is a ContentData carrying bytes -- so the string
    // checks below would reject it as the wrong type, and coercing it would mean toString() on a file. There is
    // nothing here to validate either: the content's *shape* is the MIME type's business, not JSON Schema's.
    // Presence is still enforced by the required check in the caller.
    if (jsonType == SCT.string && isBinaryFormat(type.format)) {
        return value
    }

    // A value of the wrong type is coerced, and then held to **every** check below, exactly as if it had arrived
    // as what it became (issue #815): "5" against a minimum of 10 has to fail, and so does "3" against a `const`
    // of 2 or a closed option list. It once had a short list of its own -- whitespace, characters and bounds --
    // and the `const` and `options` checks were simply never reached for it. Coercion runs in both modes (only
    // its result is discarded when validating), so this reports identically whether the caller asked for the
    // coerced value or not.
    var current = value
    if (!matchesType(jsonType, current)) {
        val before = failures.size
        val coerced = coerceMismatch(type, current, path, coerce, failures, opts)
        // Nothing further to check when the coercion failed (it said why), when it produced nothing (a blank
        // boolean), or when it produced a container: a list or map parsed from a string has been validated in
        // full already, by the call back into here that coerceMismatch makes -- checking it again would report its
        // bound failures twice.
        if (failures.size != before || !matchesType(jsonType, coerced) || jsonType == SCT.array ||
            jsonType == SCT.kObject
        ) {
            return coerced
        }
        current = coerced
    }

    // Edge whitespace is trimmed (or rejected) before any of the checks below, so `minLength`/`maxLength`,
    // `const` and `options` all measure the cleaned value -- otherwise `" a "` would pass a `minLength: 3` it
    // should fail (issue #541). On the input path a plain string with no declared mode trims by default (issue
    // #765). In validate-only mode the trimmed value is what the checks see and the caller discards it, the same
    // way `allowCoerce` validates against the coerced form without emitting it.
    val effective = applyOuterWhitespace(type, current, opts.forInput, path, failures)

    // Character rules run on every string that reached here, ahead of `const` and `options`: a value that is
    // one of the listed choices yet carries an invisible character is a broken list, and saying so beats
    // letting the list vouch for it. `pattern` is one of them (issue #823), for the same reason.
    checkVisible(type, effective, path, failures)
    checkPattern(type, effective, path, failures)

    // `const`: the type admits one value. Checked before options, and separately from them, because it is a
    // statement about the shape rather than a choice being offered -- most often a union branch saying which
    // branch it is.
    val constValue = type.constValue
    if (constValue != null && !constMatches(constValue, effective)) {
        failures.add(
            type.failure(path, SchFailCode.invalidOption, "'$effective' is not '$constValue'.",
                listOf(SchOption(constValue.toOptStr() ?: "", constValue.toOptStr() ?: "")), value = effective)
        )
        return effective
    }

    // An **open** list is suggestions rather than a bound (issue #418), so there is nothing here to check: the
    // value falls through to the ordinary type and bound checks below, exactly as an optionless field does.
    // This is the single branch that makes a per-caller list safe to vary -- one caller's suggestions can
    // never reject another caller's value, because no list of suggestions rejects anything.
    val options = type.options
    if (options != null && !type.openOptions) {
        val choice = effective as? String
        if (choice == null || options.none { it.value == choice }) {
            failures.add(type.failure(path, SchFailCode.invalidOption, "'$effective' is not a valid option.", options, value = effective))
        }
        return effective
    }
    // Measured against the instance as it arrived, which is what the bound is about -- an object's property
    // count before `emptyIsAbsent` drops anything or a default is injected. (A trimmed string is the cleaned
    // value, since edge whitespace is not content the length bound should count.)
    checkBounds(type, effective, path, failures)
    // A schema-document object is validated by parsing it, not by walking it as properties (issue #316): the
    // whole value is the candidate, and an open object with no declared properties has nothing else to check.
    if (jsonType == SCT.kObject && type.schemaDocument) {
        return validateSchemaDocument(type, effective, path, failures, opts)
    }
    return when (jsonType) {
        SCT.kObject -> validateObject(type, effective as Map<*, *>, path, coerce, failures, opts)
        SCT.array -> validateArray(type, effective as List<*>, path, coerce, failures, opts)
        else -> effective // scalar matched; a trimmed string is cleaned, everything else unchanged
    }
}

/**
 * Validates a discriminated union: read the discriminator, select one branch, validate against that branch
 * alone (issue #252).
 *
 * The failures are the selected branch's own, reported at the paths the payload actually has — which is the
 * whole reason the discriminator is declared. Trying every branch and merging what came back produces a list
 * of complaints from shapes the caller never meant to send, and no way to rank them.
 *
 * Three ways this goes wrong, each with its own answer:
 *
 * - **Not an object at all** — a plain [SchFailCode.wrongType] before anything else is attempted.
 * - **No discriminator** — [SchFailCode.missingRequired] against the discriminator's own path, since without
 *   it there is nothing to select with. Reported there rather than against the union, so the message sits on
 *   the field someone has to fill in.
 * - **A value naming no branch** — [SchFailCode.invalidOption] carrying the declared values as its options, so
 *   the existing "Valid options: …" wording lists what this reader knows. Unless a `defaultMapping` exists, in
 *   which case an unrecognized value is not an error at all: it goes to the default branch and passes through.
 */
@KdrPrivate
fun validateVariant(
    type: SchType,
    variants: SchVariants,
    value: Any?,
    path: String,
    coerce: Boolean,
    failures: MutableList<SchFailure>,
    opts: SchOpts,
): Any? {
    if (value !is Map<*, *>) {
        failures.add(type.failure(path, SchFailCode.wrongType, wrongTypeMsg(type), value = value))
        return value
    }
    val discriminatorPath = childPath(path, variants.discriminator)
    val raw = value[variants.discriminator].toOptStr()
    if (raw == null) {
        failures.add(
            type.failure(
                discriminatorPath, SchFailCode.missingRequired,
                "Required property '${variants.discriminator}' is missing.",
            ),
        )
        return value
    }
    // A strict reader declines the default branch rather than there being a second union without one; see
    // `SchOpts.allowUnknownVariant`.
    val branch = if (opts.allowUnknownVariant || variants.isKnown(raw)) variants.select(raw) else null
    if (branch == null) {
        failures.add(
            type.failure(
                discriminatorPath, SchFailCode.invalidOption, "'$raw' is not a valid option.",
                variants.values.map { SchOption(it, it) }, value = raw,
            ),
        )
        return value
    }
    return validateValue(branch, value, path, coerce, failures, opts)
}

/**
 * Whether a wire [value] matches a [declared] constant — the equality rule behind both `const` and the `if`
 * of a [SchCondition].
 *
 * Exact equality first, then equality of string form. The second half is what makes a constant usable at all
 * on a surface that loses types: a query string carries `true` as `"true"` and `42` as `"42"`, and both are
 * the same answer to the question the constant is asking. It also spans the numeric widths a JSON parser picks
 * between, where `1` and `1L` are the same number and different objects.
 *
 * **Not `toOptStr()` on both sides**, which is what this replaced and why it exists: it answered null for
 * anything which was not a `CharSequence`, so two *non-string* values both stringified to null and compared
 * equal — making a `const` of `42`, or of `true`, match absolutely anything. String constants hid it, because
 * they are the only kind anything here had until conditions arrived, and a discriminator is always a string.
 * `toOptStr` covers primitives as of issue #267, so those two cases are gone, but it still answers null for
 * anything that is not one — and the symmetry returns for a pair of objects. `fmt` never answers null, so it
 * never says "absent" about a value that was present.
 *
 * Compared through [fmt] rather than `toString`, because a bare `toString` on a `Double` **differs between
 * platforms**: `1.0` prints as `"1.0"` on the JVM and `"1"` under Kotlin/JS, and `1.0E10` as `"1.0E10"`
 * against `"10000000000"` (both measured). A kernel whose whole premise is that the two sides reach the same
 * verdict cannot compare values with a function that disagrees with itself across the wire. `fmt` routes a
 * `Double` through `fmtD`, which formats identically on both.
 */
fun constMatches(declared: Any?, value: Any?): Boolean =
    declared == value || (declared != null && value != null && declared.fmt() == value.fmt())

@KdrPrivate
fun validateObject(
    type: SchType,
    map: Map<*, *>,
    path: String,
    coerce: Boolean,
    failures: MutableList<SchFailure>,
    opts: SchOpts,
): Any {
    // Only build a new map when coercing; otherwise the input is returned untouched.
    val out: MutableMap<String, Any?>? = if (coerce) LinkedHashMap(map.size) else null
    // Keys present in the input but dropped by the emptyIsAbsent rule. Tracked separately rather than read
    // back off `out`, because validate-only mode builds no output map and the two modes have to agree about
    // which required properties were satisfied.
    val dropped = mutableSetOf<String>()

    // Declared properties first, in the order the *schema* declares them rather than the order the data
    // happened to arrive in. That makes the coerced output stable: a field cleared and later refilled reads
    // where it always did instead of moving to the end, and the payload matches the order a form renders in.
    // JSON objects have no meaningful order, so nothing downstream depends on it -- but a person reading the
    // payload does.
    for ((key, prop) in type.properties) {
        // A derived property on the way in: dropped without complaint, whether or not the caller sent it
        // (issue #254). Silently, and deliberately so -- read-modify-write is how a form works, so a client
        // echoing back a value the server computed is doing the ordinary thing, not making a mistake. It is
        // simply not their field. Contrast a privileged field, where silence would hide the case worth seeing.
        if (opts.forInput && prop.valueType.derived) {
            dropped.add(key)
            continue
        }
        if (!map.containsKey(key)) {
            continue
        }
        val v = map[key]
        // Supplied but empty: the field reads as never supplied, so it is neither validated nor written
        // out. Its `required` check is then answered below by `dropped`, not by the input's own key.
        if (isAbsentValue(prop.valueType, v)) {
            dropped.add(key)
            continue
        }
        // Call validateValue unconditionally (it collects failures); only store when coercing. Kept on its
        // own line: folding it into `out?.put(...)` would short-circuit (and skip validation) when out is null.
        // The child's completeness is relaxed iff this property is a fragment (issue #487), and reset
        // otherwise, so the relaxation covers exactly one object and never leaks into what it nests.
        val coerced = validateValue(prop.valueType, v, childPath(path, key), coerce, failures, opts.withSkipCompleteness(prop.optionalContents))
        out?.put(key, coerced)
    }

    // Then whatever the data carried that the schema does not declare, in the order it arrived.
    val mapValueType = type.additionalValueType
    // A map's entries are objects (or values) of their own, so a fragment's relaxed completeness does not carry
    // into them, as it does not into an array's elements (issue #487).
    val entryOpts = opts.withSkipCompleteness(false)
    for ((k, v) in map) {
        val key = k as? String ?: continue
        if (type.properties.containsKey(key)) {
            continue // handled above
        }
        // A map (issue #1055): every undeclared key is an entry, its value held to the map's value type and
        // reported under its own key. Before the prefix exemptions, which are a record's -- see
        // [SchType.additionalValueType]. Validated as an array's element is: as it stands, an empty value included.
        if (mapValueType != null) {
            val coerced = validateValue(mapValueType, v, childPath(path, key), coerce, failures, entryOpts)
            out?.put(key, coerced)
            continue
        }
        // Undeclared ("additional") property -- apply the additionalProperties rule with prefix exemptions.
        when {
            key.startsWith("_") -> out?.put(key, v) // off-contract, always allowed and kept
            key.startsWith("$") && key !in reservedSchemaKeys -> {
                // Off-contract annotation (e.g., $note): allowed on validating, dropped on coercing (not written to out).
            }
            type.additionalProperties -> out?.put(key, v) // allowed (incl. reserved $ keys, treated as normal)
            else -> {
                val message = if (key == SCH.dComment) commentRefusedMessage else "Additional property '$key' is not allowed."
                failures.add(SchFailure(childPath(path, key), SchFailCode.additionalProperty, message))
                // Reported either way; kept only when the caller asked. See SchOpts.keepAdditionalProperties.
                if (opts.keepAdditionalProperties) out?.put(key, v)
            }
        }
    }
    for (req in type.required) {
        if (map.containsKey(req) && req !in dropped) {
            continue
        }
        val reqType = type.properties[req]?.valueType
        // Required *of the stored shape*, not of the request: a derived property is required in the sense
        // that a complete record has one, and the server is what supplies it. Demanding it from the caller
        // would make a field they may not send the reason their request is refused (issue #254).
        if (opts.forInput && reqType?.derived == true) {
            continue
        }
        val default = reqType?.default
        // A default is injected even for a fragment (issue #487): it is a value the schema *supplies*, not a
        // demand on the caller, so waiving completeness must not silently drop it. Only the missing-required
        // *failure* is what a fragment waives -- its completeness is settled where it is assembled into a whole.
        if (default != null) {
            out?.put(req, cloneForInjection(default)) // a default supplies the value, so no failure
        } else if (!opts.skipCompleteness) {
            // Resolved against the missing property's OWN type, not this object's: the failure is reported by
            // the parent's required loop, but the copy belongs to the field it is about.
            failures.add(
                reqType?.failure(childPath(path, req), SchFailCode.missingRequired, "Required property '$req' is missing.")
                    ?: SchFailure(childPath(path, req), SchFailCode.missingRequired, "Required property '$req' is missing."),
            )
        }
    }
    // A fragment waives its conditional requiredness too, and for a sharper reason than its plain `required`:
    // the property a condition keys on may live in the stored data the fragment folds into, not in the fragment
    // itself, so "given approved, is rejectionReason required?" cannot be answered here at all (issue #487).
    // It is answered on the assembled whole -- `checkStoredEntries`, whose existence this is the reason for.
    if (!opts.skipCompleteness) type.condition?.let { checkCondition(type, it, map, dropped, path, failures) }
    return out ?: map
}

/**
 * Applies a type's `if`/`then`/`else` rule (issue #253): given what the watched property holds, some
 * properties become required and others inadmissible.
 *
 * Runs **after** the required loop and reads the same `dropped` set, so a field cleared to `""` counts as
 * absent here exactly as it does there. Otherwise, emptying a conditional field in a form would satisfy the
 * rule while leaving a key on the wire the rule forbids — the two notions of "not supplied" have to be one
 * notion.
 *
 * Both failures sit on the property they are about rather than on the object, so each lands on a field
 * somebody can go and fix.
 */
@KdrPrivate
fun checkCondition(
    type: SchType,
    condition: SchCondition,
    map: Map<*, *>,
    dropped: Set<String>,
    path: String,
    failures: MutableList<SchFailure>,
) {
    val holds = condition.holds(map)
    fun supplied(name: String) = map.containsKey(name) && name !in dropped
    for (name in condition.requiredWhen(holds)) {
        if (!supplied(name)) {
            val target = type.properties[name]?.valueType
            val at = childPath(path, name)
            val message = "Required property '$name' is missing."
            failures.add(
                target?.failure(at, SchFailCode.missingRequired, message)
                    ?: SchFailure(at, SchFailCode.missingRequired, message),
            )
        }
    }
    for (name in condition.forbiddenWhen(holds)) {
        if (supplied(name)) {
            val target = type.properties[name]?.valueType
            val at = childPath(path, name)
            // Names the field that decided, not just the one that lost: "you may not send this" is a puzzle
            // without the reason, and the reason is always another field the caller also controls.
            //
            // The two sides need opposite wording. Forbidden on the `then` side means the deciding field IS
            // the value; on the `else` side it means it is *not*, and saying "when X is true" there would
            // describe the state the caller is not in -- a message that reads as a contradiction of the form
            // in front of them.
            // `fmt`, not `toOptStr`: naming a value is not the same job as converting one. `toOptStr` still
            // answers null for anything that is not a primitive, and a rule quoted back as "empty" describes
            // itself as something nobody wrote.
            val decider = condition.value?.fmt() ?: "empty"
            val message = if (holds) {
                "'$name' is not allowed when '${condition.property}' is '$decider'."
            } else {
                "'$name' is only allowed when '${condition.property}' is '$decider'."
            }
            failures.add(
                target?.failure(at, SchFailCode.notAllowed, message, value = map[name])
                    ?: SchFailure(at, SchFailCode.notAllowed, message),
            )
        }
    }
}

/**
 * Whether [value] counts as "not supplied" for a field of [type] — the custom `emptyIsAbsent` rule.
 *
 * Empty is **zero-length**: a blank (or whitespace-only) string, an empty list, an empty map. It is never
 * zero-*valued*, so `0`, `0.0` and `false` are ordinary values. A `null` counts wherever the rule is on, which
 * also retires an old wart: null matches no `type` and coerces to nothing, so an explicit `null` in a payload
 * used to fail as [SchFailCode.wrongType] rather than reading as "no value".
 *
 * This is asked by the *container* ([validateObject]), because absence is a statement about a key. An empty
 * element inside a list is a value at its position, not a gap, so lists are never silently shortened.
 */
@KdrPrivate
fun isAbsentValue(type: SchType, value: Any?): Boolean {
    return type.emptyIsAbsent && when (value) {
        null -> true
        is CharSequence -> value.isBlank()
        is List<*> -> value.isEmpty()
        is Map<*, *> -> value.isEmpty()
        else -> false
    }
}

@KdrPrivate
fun validateArray(
    type: SchType,
    list: List<*>,
    path: String,
    coerce: Boolean,
    failures: MutableList<SchFailure>,
    opts: SchOpts,
): Any {
    val itemType = type.itemType
    val out: MutableList<Any?>? = if (coerce) ArrayList(list.size) else null
    // A fragment relaxes one object's completeness; an array's elements are objects of their own, so the flag
    // does not carry into them (issue #487). Reset here as well as at each object property, so the semantic
    // holds however the flag is placed. Normally a no-op -- `withSkipCompleteness` returns the same instance.
    val elementOpts = opts.withSkipCompleteness(false)
    // `uniqueItems` compares the elements as validated -- `"5"` coerced to `5` equals a `5` -- so when it is on
    // the elements are coerced even in validate-only mode, where only this comparison sees the result. Without
    // that, an object element would come back as it arrived (validate-only builds no output map), and two that are
    // equal only once coerced would be duplicates in one mode and not the other (issue #815). Coercing changes the
    // output, never the failures, so the elements report the same either way.
    val seen: MutableMap<String, Int>? = if (type.uniqueItems) HashMap(list.size) else null
    // Items whose type declares a `g-primaryKey` are told apart by it (issue #1521): two with one key are refused,
    // compared as validated for the reason `uniqueItems` is.
    val key = itemType?.primaryKey.orEmpty()
    val seenKeys: MutableMap<String, Int>? = if (key.isNotEmpty()) HashMap(list.size) else null
    val elementCoerce = coerce || type.uniqueItems || seenKeys != null
    list.forEachIndexed { i, elem ->
        val coerced = if (itemType != null) {
            validateValue(itemType, elem, indexPath(path, i), elementCoerce, failures, elementOpts)
        } else {
            elem
        }
        out?.add(coerced)
        // An item missing a key field has failed as incomplete already; it has no key to repeat.
        val keyValues = if (seenKeys == null) null else (coerced as? Map<*, *>)?.let { m -> key.map { m[it] } }
        if (seenKeys != null && keyValues != null && keyValues.none { it == null }) {
            val first = seenKeys.getOrPut(uniquenessKey(keyValues)) { i }
            if (first != i) {
                val named = key.zip(keyValues).joinToString(", ") { (f, v) -> "$f $v" }
                failures.add(
                    type.failure(
                        path, SchFailCode.duplicateItem,
                        "Item ${i + 1} has the key of item ${first + 1} ($named); each item's key must be different.", value = list,
                    ),
                )
            }
        }
        if (seen != null) {
            val first = seen.getOrPut(uniquenessKey(coerced)) { i }
            if (first != i) {
                failures.add(
                    type.failure(
                        path, SchFailCode.duplicateItem,
                        "Item ${i + 1} repeats item ${first + 1}; each item must be different.", value = list,
                    ),
                )
            }
        }
    }
    return out ?: list
}

/**
 * [value] as a string two JSON-equal values share and unequal ones do not (issue #823): a whole number reads the
 * same however it is held (`1`, `1L` and `1.0` are one JSON number), an object's keys are sorted (JSON objects are
 * unordered), and a string is quoted, so `"1"` and `1` stay apart.
 */
@KdrPrivate
fun uniquenessKey(value: Any?): String = when (value) {
    null -> "null"
    is String -> "\"" + value.replace("\\", "\\\\").replace("\"", "\\\"") + "\""
    is Long, is Int, is Short, is Byte -> value.toString()
    // A whole Double reads as the integer it equals; beyond Long's range it is left as a Double.
    is Number -> value.toDouble().let { if (it % 1.0 == 0.0 && abs(it) < 9.0e18) it.toLong().toString() else it.toString() }
    is Map<*, *> -> value.entries.sortedBy { it.key.toString() }
        .joinToString(",", "{", "}") { uniquenessKey(it.key.toString()) + ":" + uniquenessKey(it.value) }
    is List<*> -> value.joinToString(",", "[", "]") { uniquenessKey(it) }
    else -> value.toString()
}

/**
 * Single-pass coercion for a [value] whose type did not match [type]. Honors the custom `allowCoerce`
 * keyword: when it is off, the mismatch is a plain [SchFailCode.wrongType]; when it is on, the value's
 * content is inspected and either coerced or reported as a [SchFailCode.badValue].
 *
 * The returned value is the coerced form; callers keep it only when [coerce] is set (validate-only mode
 * discards it), so the same inspection serves both validation and output without parsing twice. For a
 * string coerced into a list or map, the parsed structure is fed back through [validateValue] so the
 * element / property schema is applied.
 */
@KdrPrivate
fun coerceMismatch(
    type: SchType, value: Any?, path: String, coerce: Boolean, failures: MutableList<SchFailure>, opts: SchOpts,
): Any? {
    if (!type.allowCoerce) {
        // A plain type check decided the value is wrong; its content was never inspected.
        failures.add(type.failure(path, SchFailCode.wrongType, wrongTypeMsg(type), value = value))
        return value
    }
    return when (type.jsonType) {
        SCT.integer -> coerceNumericString(type, value, path, failures) { it.trim().toLongOrNull() }
        SCT.number -> coerceNumericString(type, value, path, failures) { it.trim().toDoubleOrNull() }
        SCT.boolean -> coerceStringToBool(type, value, path, coerce, failures)
        SCT.string -> {
            if (value == null) {
                // Nothing to render; a plain null-vs-string type check.
                failures.add(type.failure(path, SchFailCode.wrongType, wrongTypeMsg(type), value = value))
                value
            } else {
                value.toString()
            }
        }
        SCT.array -> coerceStringToArray(type, value, path, coerce, failures, opts)
        SCT.kObject -> coerceStringToObject(type, value, path, coerce, failures, opts)
        else -> {
            failures.add(type.failure(path, SchFailCode.wrongType, wrongTypeMsg(type), value = value))
            value
        }
    }
}

/** Parses a numeric string with [parse]; a non-string is [SchFailCode.wrongType], an unparseable string
 *  [SchFailCode.badValue]. */
@KdrPrivate
fun coerceNumericString(
    type: SchType,
    value: Any?,
    path: String,
    failures: MutableList<SchFailure>,
    parse: (String) -> Any?,
): Any? {
    val s = value as? String
    if (s == null) {
        failures.add(type.failure(path, SchFailCode.wrongType, wrongTypeMsg(type), value = value))
        return value
    }
    val parsed = parse(s)
    if (parsed == null) {
        failures.add(type.failure(path, SchFailCode.badValue, "'$s' is not a valid ${type.jsonType}.", value = s))
        return value
    }
    return parsed
}

/**
 * The spellings of a boolean this layer accepts from a string, and the whole of them (issue #439).
 *
 * **A closed set, deliberately, and not [toOptBool].** That one exists for CSV and other loose sources and
 * reads only the *first character*, so `null`, `nil` and `None` all become `false` and `tremendous` becomes
 * `true` -- over-acceptance its own note owns up to, and right for the job it was built for. On a request it
 * is the wrong trade: a client that serializes a missing value as the string `"null"` would get a confident
 * `false` where a failure is the honest answer, and that is the unsafe direction for any flag whose false
 * branch does more than its true one.
 *
 * So this recognizes what somebody would actually write and refuses the rest. `on`/`off` earn their place by
 * being what an HTML checkbox submits -- the very transport the coercion default exists for, and a spelling
 * [toOptBool] happens to reject.
 *
 * Note that this keeps booleans no laxer than the numbers they now match: `toOptLong` throws on a
 * non-numeric string rather than guessing, so neither type turns nonsense into a value.
 */
private val trueSpellings = setOf("true", "t", "yes", "y", "1", "on")

/** The counterpart of [trueSpellings]; see its note for why the set is closed. */
private val falseSpellings = setOf("false", "f", "no", "n", "0", "off")

/**
 * A string read as a boolean, or null when it spells neither. Case-insensitive, and tolerant of surrounding
 * whitespace up to a space -- the same bound [toOptBool] uses, so a non-breaking space is not forgiven here
 * either.
 */
@KdrPrivate
fun parseExactBool(s: String): Boolean? {
    val v = s.trim { it <= ' ' }.lowercase()
    return when (v) {
        in trueSpellings -> true
        in falseSpellings -> false
        else -> null
    }
}

/**
 * Coerces a string to a boolean via [parseExactBool]. A recognized spelling becomes the Boolean; a
 * non-whitespace string that is not one is a [SchFailCode.badValue]; a pure-whitespace string is
 * treated as no value (null), not a failure. A non-string value is a plain [SchFailCode.wrongType].
 */
@KdrPrivate
fun coerceStringToBool(type: SchType, value: Any?, path: String, coerce: Boolean, failures: MutableList<SchFailure>): Any? {
    val s = value as? String
    if (s == null) {
        failures.add(type.failure(path, SchFailCode.wrongType, wrongTypeMsg(type), value = value))
        return value
    }
    val b = parseExactBool(s)
    if (b != null) {
        return b
    }
    if (s.any { it > ' ' }) {
        failures.add(
            type.failure(
                path,
                SchFailCode.badValue,
                "'$s' is not a recognizable boolean. Use one of: " +
                    "${trueSpellings.joinToString("/")} or ${falseSpellings.joinToString("/")}.",
                value = s,
            ),
        )
        return value
    }
    // Pure whitespace: a blank cell is treated as an absent value.
    return if (coerce) null else value
}

/**
 * Coerces a string to a list. If its first non-whitespace character is '[', it is parsed as JSON;
 * otherwise it is split on commas (see [splitComma]). The resulting list is re-validated against [type]
 * so the element schema (and any element-level coercion) is applied.
 */
@KdrPrivate
fun coerceStringToArray(
    type: SchType, value: Any?, path: String, coerce: Boolean, failures: MutableList<SchFailure>, opts: SchOpts,
): Any? {
    val s = value as? String
    if (s == null) {
        failures.add(type.failure(path, SchFailCode.wrongType, wrongTypeMsg(type), value = value))
        return value
    }
    val list: List<Any?> = if (s.firstOrNull { it > ' ' } == '[') {
        when (val parsed = s.jsonArrayResult()) {
            is Parsed.Ok -> parsed.value ?: emptyList()
            is Parsed.Failed -> {
                // The parse problem rides as the failure's cause, located, as a failed parse always has.
                val cause = parsed.problems.first().toException()
                val message = "The value is not a valid JSON array."
                failures.add(type.failure(path, SchFailCode.badValue, message, cause = cause, value = s))
                return value
            }
        }
    } else {
        s.splitComma()
    }
    return validateValue(type, list, path, coerce, failures, opts)
}

/** Coerces a string to a map by parsing it as JSON, then re-validates it against [type] so the object
 *  schema is applied. A parse failure (or a blank string) is a [SchFailCode.badValue]. */
@KdrPrivate
fun coerceStringToObject(
    type: SchType, value: Any?, path: String, coerce: Boolean, failures: MutableList<SchFailure>, opts: SchOpts,
): Any? {
    val s = value as? String
    if (s == null) {
        failures.add(type.failure(path, SchFailCode.wrongType, wrongTypeMsg(type), value = value))
        return value
    }
    val map = when (val parsed = s.jsonMapResult()) {
        is Parsed.Ok -> parsed.value
        is Parsed.Failed -> {
            val cause = parsed.problems.first().toException()
            val message = "The value is not a valid JSON object."
            failures.add(type.failure(path, SchFailCode.badValue, message, cause = cause, value = s))
            return value
        }
    }
    if (map == null) {
        failures.add(type.failure(path, SchFailCode.badValue, "The value is not a valid JSON object.", value = s))
        return value
    }
    return validateValue(type, map, path, coerce, failures, opts)
}

/**
 * Validates a `g-schemaDocument` object (issue #316): the value is a JSON Schema type body, and it is checked
 * by **parsing** it with [parseSchemaTypes] -- the same parser the schema store runs -- once its **shape** has
 * passed the generated schema for schema ([SchMetaSchema.structureFailures], issue #1056): every keyword whose
 * value is not of its shape, each a failure at its own path below the field. Parsing gets the checking the parser
 * already does and that matters -- a `oneOf` without a
 * discriminator, a branch with no `const`, a property/item/branch `$ref` to a type nothing defines -- and
 * degrades honestly: what the parser does not check, this does not claim to have checked.
 *
 * **The parser is not a complete validator, so this is not either**, and the gap is worth naming because #316
 * exists to catch bad schemas: `parseSchemaTypes` leaves a *bare, top-level* `$ref` unresolved (it resolves
 * refs only in property, item, and branch positions). A caller that needs that rejected -- the config write
 * endpoint (#613) -- has to add the check; this reports only what the parser refuses. (An unrecognized `type`
 * value was once the other gap named here; the parser refuses one now, with every other wrongly shaped keyword
 * it reads -- issue #1053.)
 *
 * The same shape as [validateDate]: parse, and turn the parser's refusal into one [SchFailCode.badValue]
 * carrying it as the cause. **One** failure for what the parser finds, not a list -- it stops at the first defect,
 * which is the trade-off of reusing it, and why the faults a shape can decide are found before it runs. A `$ref` resolves against [SchOpts.existingTypes],
 * so a caller holding a compiled store passes its types and a standalone check passes none.
 *
 * The value is returned untouched: a schema body is text to be stored as written, not a shape to coerce.
 */
fun validateSchemaDocument(
    type: SchType, value: Any?, path: String, failures: MutableList<SchFailure>, opts: SchOpts,
): Any? {
    // Guards a **direct** caller (the #613 endpoint validating a raw submitted body); unreachable from
    // [validateValue], where the kObject type check has already established a map before the short-circuit.
    val body = value as? Map<*, *>
    if (body == null) {
        failures.add(type.failure(path, SchFailCode.wrongType, "This must be a schema definition (an object).", value = value))
        return value
    }
    // Its shape first (issue #1056): every keyword at fault, each by its path, where the parse below stops at the
    // first and says it of the whole value. A body with such a fault is not parsed -- the parser would only repeat
    // one of them -- and a caller that leaves the parse to someone else still gets this much. For that caller the
    // body is a piece of a configuration, whose top may carry a directive the assembly reads (see the flag).
    val structural = SchMetaSchema.structureFailures(body.toJsonMap(), path, directivesStandAtTop = opts.schemaDocumentsUnparsed)
    if (structural.isNotEmpty()) {
        failures.addAll(structural)
        return value
    }
    if (opts.schemaDocumentsUnparsed) return value
    try {
        parseSchemaTypes(mapOf(schemaDocumentCandidate to body), opts.existingTypes)
    } catch (e: KdrException) {
        failures.add(
            type.failure(
                path, SchFailCode.badValue,
                "This is not a valid schema definition: ${e.message ?: "the parser refused it"}", cause = e, value = value,
            ),
        )
    }
    return value
}

/** The name a `g-schemaDocument` candidate is parsed under; it exists only for the duration of the parse. */
private const val schemaDocumentCandidate = "schemaDocumentCandidate"

/**
 * Validates a date-format string field, honoring **which** date format it declares (issue #189).
 *
 * `format: "date"` coerces to a [LocalDate] and `format: "date-time"` to an [Instant], because the two are
 * genuinely different things and the schema already says which one the field means. Coercing a day to an
 * instant was the old behavior, and it did not survive serialization: the instant landed at midnight in the
 * server time zone and was written back out in UTC, so `2021-06-01` returned as `2021-06-01T08:00:00.000Z` --
 * and on a server *east* of UTC the rendered day was the previous one. A [LocalDate] never acquires a zone,
 * so it round-trips as the day it is.
 *
 * A value that arrived as the *other* kind of date is **reshaped only when `allowCoerce` is on**, because
 * reshaping is itself a coercion and is governed by the same switch as every other one. So a lenient day-only
 * field narrows a timestamp to its day, while a strict one refuses it -- taking only the shape it declares.
 *
 * A parse failure is a [SchFailCode.badValue]; a value that is no kind of date is a plain
 * [SchFailCode.wrongType]. As before, the parsed result replaces the input only when [coerce] and
 * `allowCoerce` are both set.
 */
@KdrPrivate
fun validateDate(
    type: SchType, value: Any?, path: String, coerce: Boolean, failures: MutableList<SchFailure>, zone: TimeZone = serverTimeZone,
): Any? {
    val dayOnly = type.format == SFMT.date
    val lenient = type.allowCoerce

    // Already the shape this field declares: nothing to parse or reshape.
    if (if (dayOnly) value is LocalDate else value is Instant) {
        return value
    }

    val parsed: Any = when (value) {
        is String -> {
            val result = if (!dayOnly) value.parseDateResult(zone)
                else if (lenient) value.parseDayLenientResult(zone)
                else value.parseDayResult()
            when (result) {
                is Parsed.Ok -> result.value
                is Parsed.Failed -> {
                    val cause = result.problems.first().toException()
                    val message = "'$value' is not a valid date."
                    failures.add(type.failure(path, SchFailCode.badValue, message, cause = cause, value = value))
                    return value
                }
            }
        }
        is LocalDate -> if (lenient) value.toStartOfDay(zone) else {
            failures.add(type.failure(path, SchFailCode.wrongType, "This must be a timestamp, not a day.", value = value))
            return value
        }
        is Instant -> if (lenient) value.toDay(zone) else {
            failures.add(type.failure(path, SchFailCode.wrongType, "This must be a day, not a timestamp.", value = value))
            return value
        }
        else -> {
            failures.add(type.failure(path, SchFailCode.wrongType, "This must be a date string.", value = value))
            return value
        }
    }
    return if (coerce && type.allowCoerce) parsed else value
}

/** Deep-clones a default before injecting it, so the schema's value is never shared. */
@KdrPrivate
fun cloneForInjection(value: Any?): Any? = when (value) {
    is Map<*, *> -> value.deepClone()
    is List<*> -> value.deepClone()
    else -> value // scalars are immutable
}

@KdrPrivate
fun matchesType(jsonType: String?, value: Any?): Boolean = when (jsonType) {
    SCT.string -> value is String
    // "Is it an Int?" is not one question: in JS every number is one kind, so `2.5 is Int` is true there, and a
    // fraction passed for an integer in the browser that the backend then refused. So a fraction is asked after
    // too (issue #1086), and is refused on both platforms.
    //
    // One difference is left, and cannot be closed from here: a whole `Double` (`2.0`) is an integer in JS, where
    // it *is* `2`, and is not one on the JVM, where it is still a `Double`. It does not cross the wire -- the JSON
    // writer gives a whole number no fraction on either platform, and it is read back a `Long` -- so it shows only
    // to JVM code handing the validator a `Double` of its own.
    SCT.integer -> value is Long || (value is Int && isWhole(value))
    SCT.number -> value is Number
    SCT.boolean -> value is Boolean
    SCT.kObject -> value is Map<*, *>
    SCT.array -> value is List<*>
    SCT.kNull -> value == null
    else -> true // null/unknown type => no constraint
}

/** Whether [value] has no fraction and is a number at all: not NaN, not an infinity. Always true of a JVM `Int`. */
private fun isWhole(value: Number): Boolean = value.toDouble().let { it.isFinite() && it == floor(it) }

@KdrPrivate
fun wrongTypeMsg(type: SchType): String = "This must be of type '${type.jsonType ?: "any"}'."

/**
 * Checks [value] against the field's declared bounds, reporting [SchFailCode.belowMinimum] and
 * [SchFailCode.aboveMaximum] **independently**.
 *
 * Independently rather than as one "out of range": the two ends can come from different places. An overlay
 * that narrows a type may add a maximum a base schema never had, and reporting against the specific end keeps
 * the base schema's own message for the other one intact (issue #203).
 *
 * A value the field cannot measure is left alone — nothing was coerced, so there is no length or size to
 * compare, and a failure has been reported for whatever went wrong instead.
 */
@KdrPrivate
fun checkBounds(type: SchType, value: Any?, path: String, failures: MutableList<SchFailure>) {
    val min = type.minBound
    val max = type.maxBound
    if (min == null && max == null) return
    val measured = measureFor(type.jsonType, value) ?: return
    // An exclusive bound (issue #823) refuses the bound itself as well.
    if (min != null && (measured < min || type.minExclusive && measured == min)) {
        val message = boundMsg(type.jsonType, min, atLeast = true, exclusive = type.minExclusive)
        failures.add(type.failure(path, SchFailCode.belowMinimum, message, value = value))
    }
    if (max != null && (measured > max || type.maxExclusive && measured == max)) {
        val message = boundMsg(type.jsonType, max, atLeast = false, exclusive = type.maxExclusive)
        failures.add(type.failure(path, SchFailCode.aboveMaximum, message, value = value))
    }
}

/**
 * What the bound is compared against, per type: a number is itself, a string is its length, an array its
 * element count, an object its property count. Null when the value is not of the measurable shape.
 */
@KdrPrivate
fun measureFor(jsonType: String?, value: Any?): Double? = when {
    isNumericType(jsonType) -> (value as? Number)?.toDouble()
    jsonType == SCT.string -> (value as? String)?.let { codePointLength(it).toDouble() }
    jsonType == SCT.array -> (value as? List<*>)?.size?.toDouble()
    jsonType == SCT.kObject -> (value as? Map<*, *>)?.size?.toDouble()
    else -> null
}

/**
 * The custom `g-visibleOnly` rule (issue #543): a [SchFailCode.badValue] naming the first character of a
 * string [value] that has no visible rendering, or nothing when every character does. The rule is one test on
 * the Unicode General Category -- allow U+0020, refuse any other character in a `C` or `Z` category -- and
 * [firstInvisible] is where it lives.
 *
 * Reported in validate-only mode too: this is validation, not coercion, so there is no "transformed value" to
 * withhold. Its interplay with `emptyIsAbsent` is worth knowing: a value made *entirely* of invisible
 * characters (a run of no-break spaces) reads as blank, so where that rule is on the container treats it as
 * not supplied before this ever sees it -- missing if required, dropped otherwise. That is the right answer
 * either way; the value the user meant to send was nothing.
 *
 * The message names the code point in `U+XXXX` form and its 1-based position in **characters** (code points,
 * as `minLength` counts them), because the user cannot see what is wrong: the whole point of the character is
 * that it renders as nothing, or as something else.
 */
@KdrPrivate
fun checkVisible(type: SchType, value: Any?, path: String, failures: MutableList<SchFailure>) {
    if (!type.visibleOnly || value !is String) {
        return
    }
    val hit = firstInvisible(value) ?: return
    failures.add(
        type.failure(
            path, SchFailCode.badValue,
            "Character ${hit.second} at position ${hit.first} is not a visible character.", value = value,
        )
    )
}

/**
 * JSON Schema `pattern` (issue #823): a [SchFailCode.patternMismatch] when a string [value] contains no match of
 * the field's [SchType.pattern], or nothing when it does or there is none. Unanchored, as the standard has it --
 * see [SchPattern]. The message cannot say what the pattern is *for*, so it quotes it; a field whose readers are
 * not developers should say what it means in its `g-errors`.
 */
@KdrPrivate
fun checkPattern(type: SchType, value: Any?, path: String, failures: MutableList<SchFailure>) {
    val pattern = type.pattern ?: return
    if (value !is String || pattern.matches(value)) return
    failures.add(
        type.failure(path, SchFailCode.patternMismatch, "This must match the pattern '${pattern.source}'.", value = value),
    )
}

/**
 * The edge-whitespace mode actually applied to [type] on this path (issues #541, #765). It is
 * [SchType.outerWhitespace] as declared, except that a plain string field with **no** declared mode defaults to
 * [SchOuterWhitespace.trim] when [forInput], and to nothing otherwise:
 *
 *  - an explicit `"trim"` / `"reject"` applies on every path, as declared;
 *  - an explicit [SchOuterWhitespace.keep] is the opt-out -- it maps to null here, so nothing happens;
 *  - **no** keyword on a plain string trims *endpoint input* (so `minLength` / `pattern` / `options` and the
 *    handler all measure the trimmed value) and leaves output/stored values untouched.
 *
 * The input default is confined to a plain string: a date is parsed to an `Instant` and a binary is bytes, so
 * neither has edge text for the rule to see (and both return before this in [validateValue] anyway -- the guard
 * is belt-and-suspenders and documents the intent).
 */
@KdrPrivate
fun effectiveOuterWhitespace(type: SchType, forInput: Boolean): SchOuterWhitespace? {
    type.outerWhitespace?.let { return if (it == SchOuterWhitespace.keep) null else it }
    val plainString = type.jsonType == SCT.string && !isDateFormat(type.format) && !isBinaryFormat(type.format)
    return if (forInput && plainString) SchOuterWhitespace.trim else null
}

/**
 * Applies the `g-outerWhitespace` rule (issues #541, #765) to a string [value], returning the value the
 * downstream checks (`minLength`/`maxLength`, `const`, `options`) and the coerced output should use. The mode
 * is [effectiveOuterWhitespace] -- as declared, or the input-path trim default:
 *
 *  - null mode, or a non-string value -> returned unchanged;
 *  - [SchOuterWhitespace.trim] -> the trimmed string. Returned in both modes: coerce mode emits it, and
 *    validate-only checks against it and discards the return, the same contract `coerceMismatch` follows;
 *  - [SchOuterWhitespace.reject] -> the value unchanged, plus a `badValue` failure when it carries leading or
 *    trailing whitespace. "reject" never alters the value -- it only refuses it.
 *
 * "Whitespace" is the kernel's `<= ' '` test ([hasOuterWhitespace] / [trimOuterWhitespace]), not Kotlin's
 * Unicode [trim]; the two disagree on a no-break space, and the rest of the kernel uses `<= ' '`.
 */
@KdrPrivate
fun applyOuterWhitespace(type: SchType, value: Any?, forInput: Boolean, path: String, failures: MutableList<SchFailure>): Any? {
    val mode = effectiveOuterWhitespace(type, forInput) ?: return value
    if (value !is String) {
        return value
    }
    return when (mode) {
        SchOuterWhitespace.trim -> trimOuterWhitespace(value)
        SchOuterWhitespace.reject -> {
            if (hasOuterWhitespace(value)) {
                failures.add(
                    type.failure(
                        path, SchFailCode.badValue,
                        "Value has leading or trailing whitespace, which is not allowed here.", value = value,
                    )
                )
            }
            value
        }
        // effectiveOuterWhitespace maps a declared "keep" to null, so it never reaches here; the branch is for
        // the compiler's exhaustiveness, and returns the value untouched, which is what "keep" would mean.
        SchOuterWhitespace.keep -> value
    }
}

/** Whether [s] begins or ends with a whitespace character, using the kernel's `<= ' '` test (issue #541). */
@KdrPrivate
fun hasOuterWhitespace(s: String): Boolean = s.isNotEmpty() && (s.first() <= ' ' || s.last() <= ' ')

/** [s] with leading and trailing whitespace (`<= ' '`) removed -- the kernel's whitespace test (issue #541). */
@KdrPrivate
fun trimOuterWhitespace(s: String): String = s.trim { it <= ' ' }

/**
 * The first character of [s] that fails the `g-visibleOnly` rule, as (1-based position in code points,
 * `U+XXXX` spelling), or null when every character passes.
 *
 * Walked by **code point**, not by `Char`: a supplementary character (a rare ideograph, an emoji) is a
 * surrogate pair, and both halves report [CharCategory.SURROGATE], so a `Char`-wise test would refuse every
 * one of them. A properly paired surrogate is a real character; a lone one is not, and is refused as such.
 *
 * Common Kotlin cannot ask the category of a supplementary code point (there is no code-point type, only
 * `Char`), so those are admitted -- with two exceptions named by range because they are the deceptive ones:
 * the **tag characters** (U+E0000 to U+E007F, format characters used to hide text inside emoji sequences and,
 * lately, inside prompts) and the two **private-use planes** (U+F0000 to U+10FFFF), which have no defined
 * rendering at all. The handful of invisible characters that live in *visible* categories (variation
 * selectors, the Hangul fillers, the Braille blank) are deliberately not chased here; the issue lists them as
 * a possible follow-up.
 */
@KdrPrivate
fun firstInvisible(s: String): Pair<Int, String>? {
    var i = 0
    var position = 0
    while (i < s.length) {
        position++
        val ch = s[i]
        if (ch.isHighSurrogate() && i + 1 < s.length && s[i + 1].isLowSurrogate()) {
            val codePoint = 0x10000 + ((ch.code - 0xD800) shl 10) + (s[i + 1].code - 0xDC00)
            if (codePoint in 0xE0000..0xE007F || codePoint >= 0xF0000) {
                return position to codePointName(codePoint)
            }
            i += 2
            continue
        }
        if (ch != ' ' && (ch.isSurrogate() || ch.category in invisibleCategories)) {
            return position to codePointName(ch.code)
        }
        i++
    }
    return null
}

/** The Unicode General Categories with no visible rendering: the `C` family and the `Z` family. */
@KdrPrivate
val invisibleCategories: Set<CharCategory> = setOf(
    CharCategory.CONTROL, CharCategory.FORMAT, CharCategory.SURROGATE, CharCategory.PRIVATE_USE,
    CharCategory.UNASSIGNED, CharCategory.SPACE_SEPARATOR, CharCategory.LINE_SEPARATOR,
    CharCategory.PARAGRAPH_SEPARATOR,
)

/** The conventional `U+XXXX` spelling of a code point: at least four uppercase hex digits. */
@KdrPrivate
fun codePointName(codePoint: Int): String = "U+" + codePoint.toString(16).uppercase().padStart(4, '0')

/**
 * The number of Unicode code points in [s], which is what JSON Schema's `minLength`/`maxLength` count —
 * *characters*, not UTF-16 code units.
 *
 * `String.length` would be wrong for anything outside the basic plane: an emoji is one character and two code
 * units, so a three-emoji name would read as six against a `maxLength` of five and be rejected. A low
 * surrogate is always the second half of a pair, so skipping those counts pairs once.
 */
@KdrPrivate
fun codePointLength(s: String): Int {
    var n = 0
    for (ch in s) {
        if (!ch.isLowSurrogate()) n++
    }
    return n
}

/**
 * The built-in wording for a bound. The code is shared across the four pairs, but the message is not: what the
 * bound means differs by type, and "This must be at least 3." would be a poor way to say a name needs three
 * characters.
 */
@KdrPrivate
fun boundMsg(jsonType: String?, bound: Double, atLeast: Boolean, exclusive: Boolean = false): String {
    val n = bound.fmtD()
    // Only a number is ever exclusive; see [SchType.minExclusive].
    val side = when {
        exclusive -> if (atLeast) "more than" else "less than"
        else -> if (atLeast) "at least" else "at most"
    }
    val one = bound == 1.0
    return when (jsonType) {
        SCT.string -> "This must be $side $n character${if (one) "" else "s"}."
        SCT.array -> "This must have $side $n item${if (one) "" else "s"}."
        SCT.kObject -> "This must have $side $n propert${if (one) "y" else "ies"}."
        else -> "This must be $side $n."
    }
}

// These two are deliberately NOT @KdrPrivate. The spelling of a failure's path is a contract, not a detail of
// the validator: a display that wants to show a message beside the field that caused it has to build the same
// path the validator reported, and two implementations of one spelling would eventually disagree — silently,
// since the symptom is a message that quietly matches no field. So the renderer calls these rather than
// growing its own, for the same reason the frontend runs `coerceAndValidate` instead of its own validator.
// See SchFailurePath.kt for reading the paths back.

/** The path to [key] within the object at [parent]; at the root, just the key. */
fun childPath(parent: String, key: String): String = if (parent.isEmpty()) key else "$parent.$key"

/** The path to element [index] of the array at [parent]. */
fun indexPath(parent: String, index: Int): String = "$parent[$index]"
